import React, { useState } from 'react';
import { X } from 'lucide-react';
import C from '../../constants/colors';
import EmployeePicker from '../ui/EmployeePicker';

export default function UserEditorModal({ user, linkableEmployees, onSave, onClose }) {
  const isEdit = !!user;

  const available = isEdit
    ? linkableEmployees.filter(e => !e.linkedUserId || e.linkedUserId === user.id)
    : linkableEmployees.filter(e => !e.linkedUserId);

  const [employeeRecordId, setEmployeeRecordId] = useState(user?.employeeRecordId || '');
  const [email, setEmail]                       = useState(user?.email || '');
  const [role, setRole]                         = useState(user?.role || 'OPERATOR');
  const [password, setPassword]                 = useState('');
  const [saving, setSaving]                     = useState(false);
  const [error, setError]                       = useState('');

  async function handleSave() {
    if (!employeeRecordId) { setError('Please select an employee'); return; }
    if (!email.trim()) { setError('Email is required'); return; }
    if (!isEdit && !password) { setError('Password is required'); return; }
    if (password && password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { employeeRecordId, email: email.trim(), role };
      if (password) payload.password = password;
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  }

  const selectedEmployee = available.find(e => e.id === employeeRecordId);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '12px', width: '440px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: '15px', fontWeight: 700, color: C.textPrimary }}>{isEdit ? 'Edit User' : 'Add User'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '2px' }}><X size={16} /></button>
        </div>

        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

          {/* Employee selector */}
          <div>
            <label style={{ fontSize: '11px', color: C.textMuted, fontWeight: 600, display: 'block', marginBottom: '4px' }}>EMPLOYEE RECORD</label>
            {available.length === 0 && !isEdit ? (
              <div style={{ padding: '10px 12px', background: C.amber + '15', border: `1px solid ${C.amber}44`, borderRadius: '7px', fontSize: '12px', color: C.amber }}>
                All employees already have system accounts. Add new employees first.
              </div>
            ) : (
              <EmployeePicker
                employees={available}
                selectedId={employeeRecordId}
                onSelect={setEmployeeRecordId}
                placeholder="Select an employee…"
              />
            )}
            {selectedEmployee && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: C.textMuted }}>
                Account name will be set to: <strong style={{ color: C.textPrimary }}>{selectedEmployee.name}</strong>
              </div>
            )}
          </div>

          {/* Email */}
          <div>
            <label style={{ fontSize: '11px', color: C.textMuted, fontWeight: 600, display: 'block', marginBottom: '4px' }}>LOGIN EMAIL</label>
            <input
              type="email"
              value={email} onChange={e => setEmail(e.target.value)}
              placeholder="jane@company.com"
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '7px', padding: '8px 10px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {/* Admin Access toggle */}
          <div
            onClick={() => setRole(r => r === 'ADMIN' ? 'OPERATOR' : 'ADMIN')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '7px', padding: '10px 12px', cursor: 'pointer', userSelect: 'none' }}
          >
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>Admin Access</div>
              <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
                {role === 'ADMIN' ? 'Full access to all views and management features' : 'View-level permissions apply'}
              </div>
            </div>
            <div style={{ position: 'relative', width: '40px', height: '22px', flexShrink: 0, marginLeft: '12px' }}>
              <div style={{ position: 'absolute', inset: 0, borderRadius: '11px', background: role === 'ADMIN' ? C.blue : C.border, transition: 'background 0.2s' }} />
              <div style={{ position: 'absolute', top: '3px', left: role === 'ADMIN' ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            </div>
          </div>

          {/* Password */}
          <div>
            <label style={{ fontSize: '11px', color: C.textMuted, fontWeight: 600, display: 'block', marginBottom: '4px' }}>
              {isEdit ? 'NEW PASSWORD (leave blank to keep current)' : 'PASSWORD'}
            </label>
            <input
              type="password"
              value={password} onChange={e => setPassword(e.target.value)}
              placeholder={isEdit ? 'Leave blank to keep current password' : 'Min. 8 characters'}
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '7px', padding: '8px 10px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {error && <div style={{ fontSize: '12px', color: C.red }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', padding: '14px 20px', borderTop: `1px solid ${C.border}` }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '7px', border: `1px solid ${C.border}`, background: 'none', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 18px', borderRadius: '7px', border: 'none', background: C.blue, color: '#fff', fontSize: '13px', fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: 'inherit' }}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}
